/**
 * The player's hands on a fighter.
 *
 * Thin by design: it reads the action set out of the input layer and turns it
 * into the two things a fighter can currently be asked to do — move, and
 * strike. Every rule about whether those are *allowed* already lives somewhere
 * else, and this module deliberately re-implements none of it. The state
 * machine refuses a walk mid-punch; `throwStrike` refuses a punch mid-stagger;
 * `locomotion` refuses to walk through a body.
 *
 * Order matters and is the one real decision here. Strikes are read first, so
 * that on the frame a punch starts the machine is already in `strike` when
 * locomotion asks to walk — and is refused. Read the other way round, the
 * fighter would take one free step on the frame they committed to the punch.
 */

import { createLocomotion } from './locomotion.js';

/**
 * Which move each attack verb opens with.
 *
 * Only the opener is bound. Inside a cancel window the same two buttons mean
 * something else: they pick from the current move's declared follow-ups, light
 * taking the `normal` one and heavy the `strong` one. Four moves off two
 * buttons, which is how chain combos have always worked — and the reason
 * `hook` and `uppercut` are reachable at all without a third attack button.
 */
export const STRIKE_BINDINGS = Object.freeze({ heavy: 'cross', light: 'jab' });

/**
 * What the grapple button opens with when nothing more specific applies.
 *
 * `opener` is the clinch, which nearly everybody knows. It is a *default*, not
 * the whole binding: a fighter who was never taught it still has a grapple
 * button, and a fighter who knows three holds needs more than one way to ask.
 * See `grappleFor`.
 */
export const GRAPPLE_BINDINGS = Object.freeze({ opener: 'clinch', finisher: 'slam' });

/** Which verb a move answers to, by its strength. */
const verbFor = (move) => (move?.strength === 'strong' ? 'heavy' : 'light');

/**
 * The directional lanes an attack verb can be pressed in.
 *
 * Two attack buttons offer two follow-ups per cancel window, and a fighter who
 * has learned two styles can know **four** routes out of a single jab. Five of
 * the ten pairings lost a move that way, and `kickboxing+martial_arts` lost
 * four — an input problem rather than a binding one.
 *
 * The cheapest fix is the one already in the player's hand. The stick is read
 * every frame for movement, it exists on both devices without a new binding,
 * and reach already varies by direction — so a direction *plus* a verb is two
 * more slots for no new buttons, which is how this has worked in the genre
 * since the eighties.
 *
 * Order is the lane order: `neutral` takes the first route a move declares,
 * `forward` the second, `back` the third. Only two are needed by the move data
 * today — no node offers more than two follow-ups to one verb — and the third
 * is there so adding one is data rather than code.
 */
export const LANES = Object.freeze(['neutral', 'forward', 'back']);

/**
 * Which lane the stick is in.
 *
 * Forward and back only: `x` is circling, and a fighter sidestepping mid-combo
 * has not asked for a different move. The same deadzone locomotion uses, so a
 * stick at rest means the same thing to both.
 */
export function laneFrom(move, deadzone = 0.08) {
  const y = move?.y ?? 0;
  if (y > deadzone) return 'forward';
  if (y < -deadzone) return 'back';
  return 'neutral';
}

/**
 * The follow-up a verb means in a lane, out of what this move routes into.
 *
 * Falls back to the first route rather than to nothing: a player holding back
 * in a loadout that has no third route still gets their combo, because a
 * direction that means nothing here should not swallow the input.
 */
export function followFor({ follows = [], get, verb, lane = 'neutral' }) {
  const answering = follows.filter((name) => verbFor(get(name)) === verb);
  if (!answering.length) return null;
  return answering[LANES.indexOf(lane)] ?? answering[0];
}

/** Heaviest last. Damage is the only ordering a move set actually declares. */
const byWeight = (get) => (a, b) => (get(a)?.damage ?? 0) - (get(b)?.damage ?? 0);

/**
 * Which grapple the button means right now.
 *
 * One button, three slots, because a wrestler who has also learned a
 * submission knows three holds and `clinch` plus `slam` only ever offered two.
 * The attack verbs are the modifier — hold one and tap grapple — which is the
 * oldest trick in the genre and costs no new action:
 *
 *   grapple            the clinch: the hold anybody can start
 *   heavy + grapple    the heaviest hold they were taught
 *   light + grapple    the lightest of the rest
 *
 * A reeling target overrides all three, because a fighter who has just been put
 * through a wall is a window rather than a choice, and `requiresStun` is what
 * says so. Which of them is *allowed* is still `throwStrike`'s business — this
 * only decides what was asked for.
 */
export function grappleFor({
  moves = [],
  get,
  stunned = false,
  modifier = null,
  bindings = GRAPPLE_BINDINGS,
}) {
  const taught = (name) => Boolean(name) && moves.includes(name);
  const held = moves.filter((name) => get(name)?.kind === 'grapple');
  const gated = held.filter((name) => get(name)?.requiresStun).sort(byWeight(get));
  const open = held.filter((name) => !get(name)?.requiresStun).sort(byWeight(get));

  if (stunned && taught(bindings.finisher)) return bindings.finisher;
  if (stunned && gated.length) return gated.at(-1);

  // The bound opener wins when they have it, so overriding the binding still
  // changes what the button asks for — which is what the stun gate's
  // belt-and-braces test leans on.
  const opener = taught(bindings.opener) ? bindings.opener : null;
  const rest = open.filter((name) => name !== opener);
  if (modifier === 'heavy' && rest.length) return rest.at(-1);
  if (modifier === 'light' && rest.length) return rest[0];
  // No modifier, or nothing else to offer: the clinch, or the only hold they
  // have. A fighter who knows no grapple at all gets null and is refused.
  return opener ?? rest.at(-1) ?? gated.at(-1) ?? null;
}

/**
 * Which move an attack verb means, for a fighter who was never taught its
 * opener.
 *
 * Four questions, in order, and each is a step further from what the button
 * usually means:
 *
 *   1. a strike they know that answers this verb — the heaviest of them
 *   2. any strike they know that the *other* button does not already open with
 *   3. a hold they know, if strikes have run out entirely
 *   4. the opener anyway, which `throwStrike` will refuse
 *
 * Step 2 is why a submissions fighter's heavy button does something. They know
 * no strong strike at all, and before this it fell through to `cross` — a move
 * they have never been taught — so the button was dead for the whole style.
 */
export function strikeFor({ moves = [], get, verb, bindings = STRIKE_BINDINGS, knows = null }) {
  const opening = bindings[verb];
  if (!knows || knows(opening)) return opening;

  const taught = moves.filter((name) => !knows || knows(name));
  const strikes = taught.filter((name) => get(name)?.kind !== 'grapple');

  const answering = strikes.filter((name) => verbFor(get(name)) === verb).sort(byWeight(get));
  if (answering.length) return answering.at(-1);

  const otherOpener = Object.entries(bindings).find(([which]) => which !== verb)?.[1];
  const spare = strikes.filter((name) => name !== otherOpener).sort(byWeight(get));
  if (spare.length) return spare.at(-1);

  const holds = taught
    .filter((name) => get(name)?.kind === 'grapple' && !get(name)?.requiresStun)
    .sort(byWeight(get));
  return holds.at(-1) ?? opening;
}

/**
 * The input vector, turned into a direction on the floor.
 *
 * `move` is read relative to the fighter — +y forward, +x to their right — and
 * everywhere else that gets resolved against the *opponent*, because that is
 * what forward means when you are circling somebody. With one of them in your
 * hands it means your own facing instead: there is no line between you to be
 * relative to any more.
 */
export function worldDirection(facing, move) {
  const right = { x: -facing.z, z: facing.x };
  const x = facing.x * move.y + right.x * move.x;
  const z = facing.z * move.y + right.z * move.x;
  const length = Math.hypot(x, z);
  return length > 1e-6 ? { x: x / length, z: z / length } : { x: facing.x, z: facing.z };
}

/**
 * The stick that would mean `direction`, which is `worldDirection` run
 * backwards.
 *
 * A driver that knows where it wants somebody to *go* — an AI holding a
 * grapple and looking at a pillar (step 61) — still has to say so the way a
 * player says it, by pushing the stick. Writing the answer straight into
 * `grapple.whip` would be the second caller that walks around the control
 * layer, and the module header is about why there is not one.
 *
 * `facing` and its right vector are an orthonormal basis, so the inverse is
 * two dot products and no trigonometry. The vector comes back at length 1,
 * which clears the deadzone by construction — a stick pushed exactly as far as
 * the deadzone is a stick that may or may not have been pushed.
 */
export function stickToward(facing, direction) {
  const right = { x: -facing.z, z: facing.x };
  const y = facing.x * direction.x + facing.z * direction.z;
  const x = right.x * direction.x + right.z * direction.z;
  const length = Math.hypot(x, y);
  return length > 1e-6 ? { x: x / length, y: y / length } : { x: 0, y: 1 };
}

/**
 * Every move a fighter can actually get out, through the bindings.
 *
 * Written because "is there a button for this?" had no answer and the honest
 * one turned out to be no: `suplex` and `armbar` sat in two style loadouts with
 * no input path to either, and nothing noticed for eleven build steps. A move
 * set is data, the bindings are code, and the only way the two stay in
 * agreement is to ask.
 *
 * It walks what a player can do: the two attack verbs, the three grapple slots,
 * and then the cancel windows — each reachable move offering its own follow-ups
 * until nothing new turns up. Returns the set of names.
 */
export function reachableMoves({
  moves = [],
  get,
  bindings = STRIKE_BINDINGS,
  grapples = GRAPPLE_BINDINGS,
}) {
  const knows = (name) => moves.includes(name);
  const found = new Set();
  const add = (name) => {
    if (name && knows(name)) found.add(name);
  };

  for (const verb of Object.keys(bindings)) {
    add(strikeFor({ moves, get, verb, bindings, knows }));
  }
  for (const stunned of [false, true]) {
    for (const modifier of [null, 'light', 'heavy']) {
      add(grappleFor({ moves, get, stunned, modifier, bindings: grapples }));
    }
  }

  // And the chains. A cancel window offers this move's declared follow-ups, one
  // per verb *per lane* — the same `followFor` the control layer calls, so the
  // answer is what the game would actually give rather than what the routes
  // allow.
  let grew = true;
  while (grew) {
    grew = false;
    for (const from of [...found]) {
      const follows = (get(from)?.follows ?? []).filter(knows);
      for (const verb of Object.keys(bindings)) {
        for (const lane of LANES) {
          const next = followFor({ follows, get, verb, lane });
          if (next && !found.has(next)) {
            found.add(next);
            grew = true;
          }
        }
      }
    }
  }

  return found;
}

export function createPlayerControl({
  combatant,
  opponent,
  input,
  strikes = STRIKE_BINDINGS,
  /** The clinch, if there is one to escape from. See `grapple.js`. */
  grapple = null,
  /** Which grapple the button opens with, and which it reaches for on a stun. */
  grapples = GRAPPLE_BINDINGS,
  ...overrides
}) {
  const locomotion = createLocomotion({ combatant, opponent, ...overrides });

  /**
   * Verbs in priority order. Heavy is checked first: pressing both in one frame
   * is a fumble, and the committing move is the more useful thing to give back.
   */
  const order = Object.keys(strikes);

  /**
   * What this verb means right now. Inside a cancel window it is whichever of
   * the current move's follow-ups answers to it; otherwise it is the opener.
   */
  function resolve(verb, lane = laneFrom(input.move, locomotion.config.deadzone)) {
    // A weapon has one attack and both buttons throw it. There is nothing else
    // in the hand — and the alternative is a light button that does nothing,
    // because a bat answers to `heavy` and a bottle to `light`.
    if (combatant.armed) return combatant.weapon.move;

    const chain = combatant.combo;
    if (chain?.open && chain.length) {
      const from = chain.chain[chain.length - 1];
      // The lane applies **here and nowhere else**. Inside a cancel window a
      // direction disambiguates between routes that already exist; on an opener
      // it would make the most common input in the game mean different things
      // depending on whether the player happened to be walking.
      const next = followFor({
        follows: chain.follows(from),
        get: (name) => combatant.strikes.get(name),
        verb,
        lane,
      });
      if (next) return next;
    }
    // The opener, unless this fighter's styles never taught it.
    return strikeFor({
      moves: combatant.moves,
      get: (name) => combatant.strikes.get(name),
      verb,
      bindings: strikes,
      knows: combatant.knows ? (name) => combatant.knows(name) : null,
    });
  }

  /** What the grapple button means this frame, modifier and all. */
  function holdFor(modifier) {
    return grappleFor({
      moves: combatant.moves,
      get: (name) => combatant.strikes.get(name),
      stunned: Boolean(opponent?.stunned),
      modifier,
      bindings: grapples,
    });
  }

  return {
    locomotion,
    resolve,

    update(dt) {
      // Aim first. A strike freezes the fighter's facing for its whole length,
      // so the frame they commit on is the one that decides where the punch
      // goes — and on that frame it should be pointed at where the opponent is
      // now, not where they were last frame.
      locomotion.face();

      // Being held is the one situation where the controls mean something else
      // entirely: there is nothing to do but try to break out, and the same
      // button that started a clinch is the one that ends one.
      if (grapple?.isHeld(combatant)) {
        const escape = input.pressed('grapple') ? grapple.tryEscape(combatant) : 'none';
        return { escape, held: true, thrown: null, moved: 0, state: combatant.machine.state };
      }

      // Holding one is the other. The stick stops being movement and becomes
      // *aim*: there is nowhere to walk with somebody in your hands, so the only
      // thing a direction can mean is where they are going.
      if (grapple?.isHolding(combatant)) {
        const push = Math.hypot(input.move.x, input.move.y);
        const whipped =
          push > locomotion.config.deadzone
            ? grapple.whip(worldDirection(combatant.facing, input.move))
            : null;
        return {
          whipped: whipped?.outcome === 'whipped' ? whipped.direction : null,
          holding: true,
          thrown: null,
          moved: 0,
          state: combatant.machine.state,
        };
      }

      // Spending the meter costs no time and refuses itself at anything less
      // than full, so it is read before everything and gates nothing.
      let ignited = false;
      if (input.pressed('blazin')) ignited = Boolean(combatant.ignite?.());

      // Taunting is a commitment, so it goes through the state machine like an
      // attack does, and is refused mid-anything for the same reasons.
      let taunted = false;
      if (input.pressed('taunt')) taunted = Boolean(combatant.taunt?.());

      // A clinch is thrown like a strike and refused like one — and against a
      // fighter still reeling off a wall, the same button reaches for the
      // finisher instead. Whether it lands is still the reach's decision.
      //
      // This is a *choice*, not the gate. It used to be both, which meant every
      // other way of asking for a slam went straight past it — the overlay's
      // buttons, the dummy, a buffered follow-up. `requiresStun` is enforced in
      // `throwStrike` now, and what is left here is the one thing a binding
      // should decide: what the button means. Asking for the finisher when the
      // opponent is on their feet would simply be refused, and the grapple
      // button would do nothing at all.
      let clinched = null;
      if (input.pressed('grapple')) {
        // The attack verbs double as the modifier. `held`, not `pressed`: you
        // are holding one down and tapping grapple, which is how this input has
        // worked in every game that has it.
        const modifier = input.held('heavy') ? 'heavy' : input.held('light') ? 'light' : null;
        const move = holdFor(modifier);
        if (move && combatant.throwGrapple?.(move)) clinched = move;
      }

      let thrown = null;
      // A frame that threw a hold does not also throw a punch. Without this,
      // the verb being used as a modifier fires a strike of its own on the
      // frame it is first held — which the machine refuses, but which also eats
      // the buffer.
      for (const verb of clinched ? [] : order) {
        // pressed(), not held() — an attack fires on the edge, or leaning on
        // the button would machine-gun jabs for as long as it was down.
        if (!input.pressed(verb)) continue;
        const move = resolve(verb);
        // throwStrike asks the state machine, so an illegal attack fails here
        // rather than quietly working — and a refusal mid-strike is buffered
        // rather than dropped, which is what makes a cancel window catchable.
        if (combatant.throwStrike(move)) thrown = move;
        break;
      }

      const moved = locomotion.update(dt, {
        move: input.move,
        run: input.held('run'),
        // Block is a hold, not a press: the guard is up for exactly as long as
        // the button is down, and comes off the frame it is released.
        guard: input.held('block'),
      });
      return { thrown, ignited, taunted, clinched, ...moved };
    },
  };
}
